import { MerchantFeature } from "@redotech/cockroachdb-codegen/tables/internal-ops/merchant-feature";
import { merchantPausedFeatureIdFormat } from "@redotech/redo-merchant-pause-id";
import * as z from "zod";

/** Longest a feature may be paused in one stretch; mirrors the DB check. */
export const MAX_MERCHANT_FEATURE_PAUSE_DAYS = 10;
export const MAX_MERCHANT_FEATURE_PAUSE_MS =
  MAX_MERCHANT_FEATURE_PAUSE_DAYS * 24 * 60 * 60 * 1000;

export const MerchantPausedFeatureSchema = z.object({
  id: merchantPausedFeatureIdFormat.schema,
  teamId: z.string(),
  feature: z.enum(MerchantFeature),
  startsAt: z.date(),
  endsAt: z.date(),
  reason: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type MerchantPausedFeature = z.infer<typeof MerchantPausedFeatureSchema>;

export type PauseWindow = Pick<MerchantPausedFeature, "startsAt" | "endsAt">;

export enum PauseWindowError {
  START_IN_PAST = "start_in_past",
  END_NOT_AFTER_START = "end_not_after_start",
  EXCEEDS_MAX_DURATION = "exceeds_max_duration",
}

/**
 * Why a requested pause window is not allowed, or null when it is. A pause can
 * only start now or later so the table never claims coverage was off in the
 * past. The DB check `merchant_paused_feature_window` covers the rest; this
 * exists so callers can reject before the round trip with a specific reason.
 */
export function validatePauseWindow(
  { startsAt, endsAt }: PauseWindow,
  now: Date,
): PauseWindowError | null {
  if (startsAt.getTime() < now.getTime()) {
    return PauseWindowError.START_IN_PAST;
  }
  if (endsAt.getTime() <= startsAt.getTime()) {
    return PauseWindowError.END_NOT_AFTER_START;
  }
  if (endsAt.getTime() - startsAt.getTime() > MAX_MERCHANT_FEATURE_PAUSE_MS) {
    return PauseWindowError.EXCEEDS_MAX_DURATION;
  }
  return null;
}

/** Start is inclusive, end is exclusive: paused iff starts_at <= now < ends_at. */
export function isPauseActive(pause: PauseWindow, now: Date): boolean {
  return (
    pause.startsAt.getTime() <= now.getTime() &&
    now.getTime() < pause.endsAt.getTime()
  );
}

export enum PauseStatus {
  SCHEDULED = "scheduled",
  ACTIVE = "active",
  ENDED = "ended",
}

export function pauseStatus(pause: PauseWindow, now: Date): PauseStatus {
  if (now.getTime() < pause.startsAt.getTime()) {
    return PauseStatus.SCHEDULED;
  }
  return isPauseActive(pause, now) ? PauseStatus.ACTIVE : PauseStatus.ENDED;
}
